import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import KpiTile from './components/KpiTile';
import StatusChip from './components/StatusChip';
import AgeBadge from './components/AgeBadge';
import { fetchAgeing, updateSampleStatus } from './api';
import { useFilters } from './FilterContext';
import { useMasters } from './MastersContext';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const MARKET_BADGE = {
  US: { bg: '#dbeafe', text: '#1d4ed8' },
  EU: { bg: '#d1fae5', text: '#065f46' },
  EP: { bg: '#ede9fe', text: '#5b21b6' },
};

const SampleAgeing = () => {
  const { filters, buildQueryParams } = useFilters();
  const { ageingBands } = useMasters();

  const [ageingData, setAgeingData]   = useState(null);
  const [loading, setLoading]         = useState(false);
  // accordion: all expanded by default; keyed by band.id
  const [expanded, setExpanded]       = useState({});

  const toggleBand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const loadAgeing = async () => {
    setLoading(true);
    try {
      const result = await fetchAgeing(buildQueryParams());
      setAgeingData(result);
      // default all bands collapsed on first load
      setExpanded(prev => {
        const next = { ...prev };
        (result?.bands || []).forEach(b => { if (next[b.id] === undefined) next[b.id] = false; });
        return next;
      });
    } catch {
      message.error('Failed to load ageing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAgeing(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (id, status) => {
    try {
      await updateSampleStatus(id, status);
      loadAgeing();
    } catch { message.error('Failed to update status'); }
  };

  const renderActions = (record) => {
    if (record.status === 'Completed' || record.status === 'Cancelled') return null;
    return (
      <div style={{ display: 'flex', gap: 3 }}>
        {record.status === 'Pending' && (
          <button onClick={() => handleAction(record.id, 'Running')} style={{
            fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 3,
            border: 'none', cursor: 'pointer', background: '#fffbeb', color: '#d97706',
          }}>RUN</button>
        )}
        {record.status === 'Running' && (
          <button onClick={() => handleAction(record.id, 'Completed')} style={{
            fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 3,
            border: 'none', cursor: 'pointer', background: '#f0fdf4', color: '#059669',
          }}>DONE</button>
        )}
        <button onClick={() => handleAction(record.id, 'Cancelled')} style={{
          fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 3,
          border: 'none', cursor: 'pointer', background: '#fef2f2', color: '#dc2626',
        }}>CANC</button>
      </div>
    );
  };

  // Merge API data with master band definitions
  const apiBands    = ageingData?.bands || [];
  const openSamples = ageingData?.openSamples || [];

  const bands = (ageingBands || []).map(band => {
    const apiData     = apiBands.find(b => b.id === band.id) || {};
    const bandSamples = openSamples.filter(s => s.band && s.band.id === band.id);
    return {
      ...band,
      total:    apiData.total    || 0,
      openCount: apiData.openCount || 0,
      avgTat:   apiData.avgTat   || 0,
      samples:  bandSamples,
    };
  });

  /* -------- KPI Tiles (vertical stack for left panel) -------- */
  const renderKpiTiles = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {bands.map(band => (
        <KpiTile
          key={band.id}
          label={`${band.name} (${band.minDays}-${band.maxDays}d)`}
          value={band.total}
          subLabel={`${band.openCount} still open`}
          accentColor={band.colorHex}
        />
      ))}
    </div>
  );

  /* -------- Charts -------- */
  const renderAgeDistribution = () => {
    const allOpen   = openSamples.filter(s => s.status !== 'Completed' && s.status !== 'Cancelled');
    const maxAge    = allOpen.reduce((m, s) => Math.max(m, s.ageDays || 0), 0);
    const bucketCnt = Math.max(Math.ceil((maxAge + 1) / 7), 1);
    const buckets   = Array.from({ length: bucketCnt }, (_, i) => {
      const min = i * 7, max = min + 6, mid = min + 3;
      const cnt  = allOpen.filter(s => s.ageDays >= min && s.ageDays <= max).length;
      const band = ageingBands.find(b => mid >= b.minDays && mid <= b.maxDays);
      return { label: `${min}-${max}`, count: cnt, color: band ? band.colorHex : '#9ca3af' };
    });

    const rawMax = Math.max(...buckets.map(b => b.count), 1);
    const step = rawMax <= 10 ? 2 : rawMax <= 50 ? 10 : rawMax <= 200 ? 50 : 100;
    const yMax = Math.ceil(rawMax / step) * step;
    const yTicks = [];
    for (let v = 0; v <= yMax; v += step) yTicks.push(v);

    const W = 420, H = 150;
    const PAD = { top: 16, right: 8, bottom: 30, left: 28 };
    const chartW = W - PAD.left - PAD.right;
    const chartH = H - PAD.top - PAD.bottom;
    const barGroupW = buckets.length ? chartW / buckets.length : chartW;
    const barW = Math.max(Math.min(barGroupW * 0.7, 28), 4);
    const yScale = v => chartH - (v / yMax) * chartH;

    return (
      <div style={{
        background: '#fff', borderRadius: 6, border: '1px solid #d1d5db',
        padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,.06)',
        height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header row: title + legend */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Age Distribution (7-day buckets)
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {ageingBands.map(band => (
              <div key={band.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: band.colorHex, flexShrink: 0 }} />
                <span style={{ fontSize: 8, color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {band.name} ({band.minDays}–{band.maxDays}d)
                </span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: '#9ca3af', flexShrink: 0 }} />
              <span style={{ fontSize: 8, color: '#6b7280' }}>Unclassified</span>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" style={{ display: 'block', overflow: 'visible' }}>
            {/* Y-axis grid + labels */}
            {yTicks.map(v => {
              const y = PAD.top + yScale(v);
              return (
                <g key={v}>
                  <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
                    stroke={v === 0 ? '#d1d5db' : '#f0f1f3'}
                    strokeWidth={v === 0 ? 1 : 0.75} />
                  <text x={PAD.left - 4} y={y + 3} textAnchor="end"
                    fontSize={7} fill="#9ca3af" fontFamily="'JetBrains Mono', monospace">
                    {v}
                  </text>
                </g>
              );
            })}

            {/* Y-axis line */}
            <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH}
              stroke="#d1d5db" strokeWidth={1} />

            {/* Bars */}
            {buckets.map((b, i) => {
              const cx = PAD.left + i * barGroupW + barGroupW / 2;
              const x = cx - barW / 2;
              const barH = b.count > 0 ? Math.max((b.count / yMax) * chartH, 3) : 0;
              const barY = PAD.top + chartH - barH;
              return (
                <g key={i}>
                  {barH > 0 && (
                    <rect x={x} y={barY} width={barW} height={barH}
                      fill={b.color} rx={2} ry={2} />
                  )}
                  {b.count > 0 && (
                    <text x={cx} y={barY - 3} textAnchor="middle"
                      fontSize={7.5} fontWeight="700" fill="#374151"
                      fontFamily="'JetBrains Mono', monospace">
                      {b.count}
                    </text>
                  )}
                  <text x={cx} y={PAD.top + chartH + 11} textAnchor="middle"
                    fontSize={7} fill="#9ca3af" fontFamily="'DM Sans', sans-serif">
                    {b.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };


  /* -------- Accordion band tables -------- */
  const renderBandTables = () =>
    bands.map(band => {
      const sorted   = [...band.samples].sort((a, b) => (b.ageDays || 0) - (a.ageDays || 0));
      const isOpen   = expanded[band.id] === true; // default collapsed
      const hasItems = sorted.length > 0;

      return (
        <div key={band.id} style={{
          background: '#fff', borderRadius: 6,
          border: '1px solid #d1d5db',
          borderLeft: `4px solid ${band.colorHex}`,
          boxShadow: '0 1px 3px rgba(0,0,0,.06)',
          marginBottom: 8, overflow: 'hidden',
        }}>
          {/* Accordion header — always clickable */}
          <div
            onClick={() => toggleBand(band.id)}
            style={{
              padding: '9px 14px',
              background: isOpen ? '#fff' : '#fafbfc',
              borderBottom: isOpen ? '1px solid #e5e7eb' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer', userSelect: 'none',
              transition: 'background 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: band.colorHex, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1a3353' }}>
                {band.name}
              </span>
              <span style={{ fontSize: 11, color: '#6b7280' }}>
                {band.minDays}–{band.maxDays} days
              </span>
              {/* Stats row */}
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, background: '#f3f4f6',
                  border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 8px', color: '#374151',
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {band.total} total
                </span>
                {band.openCount > 0 && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, background: band.colorHex + '22',
                    border: `1px solid ${band.colorHex}66`, borderRadius: 4,
                    padding: '1px 8px', color: band.colorHex,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {band.openCount} open
                  </span>
                )}
                {band.avgTat > 0 && (
                  <span style={{ fontSize: 11, color: '#6b7280' }}>
                    avg TAT: <strong style={{ color: '#1a3353', fontFamily: "'JetBrains Mono', monospace" }}>{Number(band.avgTat).toFixed(1)}d</strong>
                  </span>
                )}
              </div>
            </div>

            {/* Chevron */}
            <svg
              width="14" height="14" viewBox="0 0 14 14" fill="none"
              style={{ transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
            >
              <path d="M3 5l4 4 4-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          {/* Collapsible content */}
          {isOpen && (
            <div style={{ overflowX: 'auto' }}>
              {hasItems ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, whiteSpace: 'nowrap' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fb' }}>
                      {['AR No.', 'Product', 'Test', 'Market', 'Priority', 'Status', 'Age', 'Actions'].map(h => (
                        <th key={h} style={{
                          padding: '6px 10px', fontSize: 10, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: 0.3,
                          color: '#6b7280', textAlign: 'left',
                          borderBottom: '1px solid #e5e7eb',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((s, idx) => {
                      const mb = MARKET_BADGE[s.market] || { bg: '#f3f4f6', text: '#6b7280' };
                      return (
                        <tr key={s.id} style={{ background: idx % 2 === 1 ? '#fafbfc' : '#fff' }}>
                          <td style={{ padding: '5px 10px', borderBottom: '1px solid #f0f0f0' }}>
                            <span style={{ ...MONO, fontSize: 10, color: '#1a3353', fontWeight: 700 }}>{s.arNumber}</span>
                          </td>
                          <td style={{ padding: '5px 10px', borderBottom: '1px solid #f0f0f0', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.productName}
                          </td>
                          <td style={{ padding: '5px 10px', borderBottom: '1px solid #f0f0f0', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.testName}
                          </td>
                          <td style={{ padding: '5px 10px', borderBottom: '1px solid #f0f0f0' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 7px', borderRadius: 3,
                              fontSize: 10, fontWeight: 700, background: mb.bg, color: mb.text,
                            }}>{s.market}</span>
                          </td>
                          <td style={{ padding: '5px 10px', borderBottom: '1px solid #f0f0f0' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 7px', borderRadius: 3,
                              fontSize: 10, fontWeight: 700,
                              background: s.priority === 'Urgent' ? '#fff7ed' : '#f3f4f6',
                              color:      s.priority === 'Urgent' ? '#ea580c' : '#6b7280',
                            }}>{s.priority}</span>
                          </td>
                          <td style={{ padding: '5px 10px', borderBottom: '1px solid #f0f0f0' }}>
                            <StatusChip status={s.status} />
                          </td>
                          <td style={{ padding: '5px 10px', borderBottom: '1px solid #f0f0f0' }}>
                            <AgeBadge ageDays={s.ageDays} ageingBands={ageingBands} />
                          </td>
                          <td style={{ padding: '5px 10px', borderBottom: '1px solid #f0f0f0' }}>
                            {renderActions(s)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: '14px 16px', fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                  No open samples in this band
                </div>
              )}
            </div>
          )}
        </div>
      );
    });

  if (loading && !ageingData) {
    return <div style={{ padding: 24, color: '#6b7280', fontSize: 13 }}>Loading ageing data…</div>;
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 24px' }}>
      {/* Top section: equal 50/50 grid — KPI tiles left, chart right */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        {/* Left: KPI band tiles stacked, fill height */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {renderKpiTiles()}
        </div>
        {/* Right: chart fills the same height as tiles */}
        <div style={{ minHeight: 0 }}>
          {renderAgeDistribution()}
        </div>
      </div>
      {renderBandTables()}
    </div>
  );
};

export default SampleAgeing;
